import React, { useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Progress,
  Icon,
  IconButton,
  Collapse,
  Tooltip,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiZap, FiClock, FiXCircle } from 'react-icons/fi';
import moment from 'moment';
import { STATUS_COLOR, getNodeLabel, getSummary } from './campaignConstants';
import CampaignProgressFlow from './CampaignProgressFlow';

const ENROLLMENT_STATUS_LABEL = {
  active: 'Active',
  completed: 'Completed',
  paused: 'Paused',
  failed: 'Failed',
};

const ENROLLMENT_STATUS_COLOR = {
  active: 'blue',
  completed: 'green',
  paused: 'gray',
  failed: 'red',
};

export default function CampaignEnrollmentCard({ enrollment, onRemove, ownerName = null }) {
  const { isOpen, onToggle } = useDisclosure();
  const [isRemoving, setIsRemoving] = useState(false);
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.750');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const {
    userCampaign,
    template,
    currentStep,
    progress,
    status,
    enrolledAt,
    nodes,
    actionNodes,
    completedNodeIds,
    failedNodeIds,
    nodeQueueMap,
  } = enrollment;

  const canRemove = onRemove && (status === 'active' || status === 'paused');

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (!canRemove) return;
    setIsRemoving(true);
    try {
      await onRemove(userCampaign.id);
      toast({ title: 'Removed from campaign', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Failed to remove', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setIsRemoving(false);
    }
  };

  const currentStepLabel = currentStep?.node
    ? `${getNodeLabel(currentStep.node.subType)} — ${getSummary(currentStep.node)}`
    : null;

  const isWaiting =
    currentStep?.queueItem?.status === 'pending' &&
    currentStep?.queueItem?.executeAfter &&
    moment(currentStep.queueItem.executeAfter).isAfter(moment());

  const waitLabel = isWaiting
    ? `Waiting until ${moment(currentStep.queueItem.executeAfter).format('MMM D, h:mm A')}`
    : null;

  return (
    <Box
      border="1px solid"
      borderColor={isOpen ? ENROLLMENT_STATUS_COLOR[status] + '.300' : borderColor}
      borderRadius="lg"
      bg={cardBg}
      overflow="hidden"
      transition="all 0.15s"
      _dark={{
        borderColor: isOpen ? ENROLLMENT_STATUS_COLOR[status] + '.600' : 'gray.600',
      }}
    >
      {/* Collapsed header — always visible */}
      <Box
        px={4}
        py={3}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: hoverBg }}
        transition="background 0.1s"
      >
        <HStack justify="space-between">
          <HStack spacing={3} flex="1" minW={0}>
            <Icon as={FiZap} color="blue.400" flexShrink={0} />
            <VStack align="start" spacing={0} minW={0}>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                  {template.name || 'Unnamed Campaign'}
                </Text>
                <Badge
                  colorScheme={ENROLLMENT_STATUS_COLOR[status]}
                  fontSize="2xs"
                  variant="subtle"
                >
                  {ENROLLMENT_STATUS_LABEL[status] || status}
                </Badge>
                {ownerName && (
                  <Tooltip label={`This enrollment runs under ${ownerName}'s campaign`} fontSize="xs">
                    <Badge colorScheme="gray" fontSize="2xs" variant="outline">
                      via {ownerName}
                    </Badge>
                  </Tooltip>
                )}
              </HStack>

              {/* Current step or waiting label */}
              {currentStepLabel && (
                <HStack spacing={1} mt={0.5}>
                  {isWaiting && <Icon as={FiClock} color="orange.400" boxSize="11px" />}
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {waitLabel || currentStepLabel}
                  </Text>
                </HStack>
              )}
              {status === 'completed' && !currentStepLabel && (
                <Text fontSize="xs" color="green.500">
                  All steps completed
                </Text>
              )}
            </VStack>
          </HStack>

          <HStack spacing={2} flexShrink={0}>
            {/* Progress */}
            <VStack spacing={0} align="end" minW="80px">
              <Text fontSize="2xs" color="gray.500" whiteSpace="nowrap">
                {progress.completed} of {progress.total} steps
              </Text>
              <Progress
                value={progress.percentage}
                size="xs"
                colorScheme={ENROLLMENT_STATUS_COLOR[status]}
                borderRadius="full"
                w="80px"
                mt={0.5}
              />
            </VStack>
            {canRemove && (
              <Tooltip label="Remove from campaign" fontSize="xs">
                <IconButton
                  icon={<FiXCircle />}
                  aria-label="Remove from campaign"
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  isLoading={isRemoving}
                  onClick={handleRemove}
                />
              </Tooltip>
            )}
            <Icon
              as={isOpen ? FiChevronUp : FiChevronDown}
              color="gray.400"
              boxSize="16px"
            />
          </HStack>
        </HStack>

        {/* Enrolled date */}
        {enrolledAt && (
          <Text fontSize="2xs" color="gray.400" mt={1} pl="28px">
            Enrolled {moment(enrolledAt).format('MMM D, YYYY [at] h:mm A')}
          </Text>
        )}
      </Box>

      {/* Expanded detail — campaign progress flow */}
      <Collapse in={isOpen} animateOpacity>
        <Box
          px={4}
          pb={4}
          pt={1}
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <CampaignProgressFlow
            nodes={nodes}
            actionNodes={actionNodes}
            completedNodeIds={completedNodeIds}
            failedNodeIds={failedNodeIds}
            currentStep={currentStep}
            nodeQueueMap={nodeQueueMap}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
